import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { sessionFromToken } from '../api/auth';
import { useAuth } from '../hooks/useAuth';

export default function ExternalAuthPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();

    const token = searchParams.get('token') ?? '';
    const returnUrl = searchParams.get('returnUrl') ?? '/badges/create';

    const [error, setError] = useState('');

    useEffect(() => {
        if (!token) {
            setError('No authentication token was provided. Please try again from your learning platform.');
            return;
        }

        let cancelled = false;

        (async () => {
            const res = await sessionFromToken(token);
            if (cancelled) return;

            if (res.success) {
                await refresh();
                if (!cancelled) navigate(returnUrl, { replace: true });
            } else {
                setError(res.message ?? 'The authentication link has expired or is invalid. Please try again from your learning platform.');
            }
        })();

        return () => { cancelled = true; };
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    if (!error) {
        return (
            <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="background.default">
                <Box textAlign="center">
                    <CircularProgress size={48} sx={{ color: 'accent.main' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                        Signing you in…
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="background.default">
            <Card sx={{ width: 400, border: 'none' }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main' }} />
                    <Typography variant="h6" fontWeight={700} mt={1} mb={1}>
                        Sign-in Failed
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                        {error}
                    </Typography>
                    <Button component={Link} to="/login" variant="contained" fullWidth>
                        Go to Login
                    </Button>
                </CardContent>
            </Card>
        </Box>
    );
}
